import axios from "axios";
import { peekApiCache, writeApiCache } from "../hooks/useApiResource";
import type { PlayerEpicStatsResponse } from "../types/epicLeaderboard";

const cacheKey = (player: string, startDate?: string) =>
  `epic-player|${player.toLowerCase()}|${startDate ?? ""}`;

// Already-fetched epic stats for this player + range, if any.
export const peekPlayerEpicStats = (
  player: string,
  startDate?: string
): PlayerEpicStatsResponse | null =>
  peekApiCache<PlayerEpicStatsResponse>(cacheKey(player, startDate));

// Epic stats for a player. A player the API has never heard of just has no
// runs. Shared by the Overview's best-times list and the Epics tab, so
// switching between them doesn't refetch.
export const fetchPlayerEpicStats = async (
  player: string,
  startDate: string | undefined,
  signal?: AbortSignal
): Promise<PlayerEpicStatsResponse> => {
  const key = cacheKey(player, startDate);
  const hit = peekApiCache<PlayerEpicStatsResponse>(key);
  if (hit) return hit;

  const params: Record<string, string> = { player };
  if (startDate) params.startDate = startDate;
  try {
    const res = await axios.get<PlayerEpicStatsResponse>(
      `${process.env.REACT_APP_API}public/epic-leaderboard/player`,
      { params, signal }
    );
    writeApiCache(key, res.data);
    return res.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      const empty = { player, epics: [] };
      writeApiCache(key, empty);
      return empty;
    }
    throw err;
  }
};
